import * as React from 'react';

import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {PhoneMessagePreview} from 'shells/sms-campaigns/message-preview/phone-message-preview';
import {SectionLabel} from 'shells/typography/section-label';
import {useDebounce} from 'shells/utils/hooks/use-debounce';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {
    SmsBroadcastFragment as Broadcast,
    SmsEditionUpdateInput,
} from 'nutshell-graphql-types';

import {BroadcastDetailsForm} from './broadcast-details-form';

type Props = {
    broadcast: Broadcast;
    prepareToSendPath: string;
    onUpdate: (input: SmsEditionUpdateInput) => Promise<any>;
};

export function BroadcastDetails(props: Props) {
    const [content, setContent] = React.useState(props.broadcast.edition.content);
    const [isSavingBroadcastDetails, setIsSavingBroadcastDetails] = React.useState(false);

    const {toastState} = useManageSaveState(isSavingBroadcastDetails, 1000);
    const debouncedContent = useDebounce(content, 300);

    const handleUpdate = (input: SmsEditionUpdateInput): Promise<any> => {
        setIsSavingBroadcastDetails(true);

        return props.onUpdate(input).then(() => {
            setIsSavingBroadcastDetails(false);
        });
    };

    useDidMountEffect(() => {
        handleUpdate({editionId: props.broadcast.edition.id, content: debouncedContent});
    }, [debouncedContent]);

    return (
        <PageContentTwoColumn
            withoutOuterPadding={true}
            leftContent={
                <>
                    <SectionLabel>Preview</SectionLabel>
                    <div className='full-width full-height background-offwhite flex justify-center align-center'>
                        <PhoneMessagePreview
                            message={content}
                            optOutText={props.broadcast.edition.optOutText || undefined}
                        />
                    </div>
                </>
            }
            rightContent={
                <>
                    <SectionLabel>
                        <SavedSettingToast state={toastState}>Broadcast details</SavedSettingToast>
                    </SectionLabel>
                    <BroadcastDetailsForm
                        broadcast={props.broadcast}
                        prepareToSendPath={props.prepareToSendPath}
                        content={content}
                        onContentChange={setContent}
                        onIsSavingDetailsChange={setIsSavingBroadcastDetails}
                        onUpdate={handleUpdate}
                    />
                </>
            }
        />
    );
}
